// Select only standard favicon links (avoid removing apple-touch, mask icons)
const ICON_LINK_SELECTOR = 'link[rel~="icon"], link[rel="shortcut icon"]';

// The app's index.html icon links, captured before the first replacement so
// they can be put back when a client icon no longer applies.
let defaultIconLinks: HTMLLinkElement[] | null = null;

const getIconLinks = () =>
  Array.from(document.querySelectorAll<HTMLLinkElement>(ICON_LINK_SELECTOR));

/**
 * Updates the page favicon with the provided URL
 */
export const updateFavicon = (url: string) => {
  if (typeof document === "undefined") return;

  const resolvedUrl = new URL(url, document.baseURI).href;
  const iconLinks = getIconLinks();

  // Idempotency: if a favicon with the same URL already exists, skip
  if (iconLinks.some((el) => el.href === resolvedUrl)) {
    return;
  }

  if (defaultIconLinks === null) {
    defaultIconLinks = iconLinks.map((el) => el.cloneNode() as HTMLLinkElement);
  }

  // Remove existing favicon links
  iconLinks.forEach((el) => el.remove());

  // Create and add new favicon
  const link = document.createElement("link");
  link.rel = "icon";
  link.href = resolvedUrl;

  // Provide a type hint based on file extension
  const ext = resolvedUrl
    .split("?")[0]
    .split("#")[0]
    .split(".")
    .pop()
    ?.toLowerCase();
  if (ext) {
    const mimeByExt: Record<string, string> = {
      svg: "image/svg+xml",
      png: "image/png",
      ico: "image/x-icon",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      webp: "image/webp",
      gif: "image/gif",
    };
    if (mimeByExt[ext]) {
      link.type = mimeByExt[ext];
    }
  }
  document.head.appendChild(link);
};

/**
 * Restores the favicon links from the app's index.html
 */
export const restoreDefaultFavicon = () => {
  if (typeof document === "undefined" || defaultIconLinks === null) return;

  const iconLinks = getIconLinks();
  const defaults = defaultIconLinks;

  // Idempotency: skip when the defaults are already in place
  if (
    iconLinks.length === defaults.length &&
    iconLinks.every((el, index) => el.href === defaults[index].href)
  ) {
    return;
  }

  iconLinks.forEach((el) => el.remove());
  defaults.forEach((el) => document.head.appendChild(el.cloneNode()));
};

/**
 * Removes the current favicon
 */
export const removeFavicon = () => {
  if (typeof document === "undefined") return;

  getIconLinks().forEach((el) => el.remove());
};
